import React, { useState } from 'react';
import type { SystemSettings as SystemSettingsType } from '../../types';
import { GeneralSettings } from './GeneralSettings';
import { SecuritySettings } from './SecuritySettings';
import { EmailSettings } from './EmailSettings';
import { BackupSettings } from './BackupSettings';

const defaultSettings: SystemSettingsType = {
  general: {
    companyName: '',
    timeZone: 'utc',
    unit: 'metric'
  },
  security: {
    twoFactorAuth: false,
    passwordStrength: 'strong'
  },
  email: {
    smtpServer: '',
    smtpPort: 587,
    fromAddress: '',
    fromName: ''
  },
  backup: {
    autoBackup: false,
    backupFrequency: 'daily',
    retentionDays: 30
  }
};

export const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettingsType>(defaultSettings);

  const handleGeneralSettingChange = (field: keyof SystemSettingsType['general'], value: string) => {
    setSettings(prev => ({
      ...prev,
      general: {
        ...prev.general,
        [field]: value
      }
    }));
  };

  const handleSecuritySettingChange = (field: keyof SystemSettingsType['security'], value: boolean | SystemSettingsType['security']['passwordStrength']) => {
    setSettings(prev => ({
      ...prev,
      security: {
        ...prev.security,
        [field]: value
      }
    }));
  };

  const handleEmailSettingChange = (field: keyof SystemSettingsType['email'], value: string | number) => {
    setSettings(prev => ({
      ...prev,
      email: {
        ...prev.email,
        [field]: value
      }
    }));
  };

  const handleBackupSettingChange = (field: keyof SystemSettingsType['backup'], value: boolean | string | number) => {
    setSettings(prev => ({
      ...prev,
      backup: {
        ...prev.backup,
        [field]: value
      }
    }));
  };

  const handleBackupNow = () => {
    // TODO: Implement backup functionality
    console.log('Backing up system...');
  };

  return (
    <div className="space-y-8">
      <GeneralSettings
        companyName={settings.general.companyName}
        timeZone={settings.general.timeZone}
        unit={settings.general.unit}
        onCompanyNameChange={(value) => handleGeneralSettingChange('companyName', value)}
        onTimeZoneChange={(value) => handleGeneralSettingChange('timeZone', value)}
        onUnitChange={(value) => handleGeneralSettingChange('unit', value)}
      />

      <SecuritySettings
        twoFactorAuth={settings.security.twoFactorAuth}
        passwordStrength={settings.security.passwordStrength}
        onTwoFactorAuthChange={(value) => handleSecuritySettingChange('twoFactorAuth', value)}
        onPasswordStrengthChange={(value) => handleSecuritySettingChange('passwordStrength', value)}
      />

      <EmailSettings
        settings={settings.email}
        onSettingChange={handleEmailSettingChange}
      />

      <BackupSettings
        settings={settings.backup}
        onSettingChange={handleBackupSettingChange}
        onBackupNow={handleBackupNow}
      />
    </div>
  );
}; 